import React, { useState, useEffect, useRef, useMemo } from 'react';
import './allInquiries.css';

const PAGE_SIZE = 20;

const API_ENDPOINTS = {
  all: 'https://car.amar.co.jp/api/authorization/admin-dashboard-panel/get-all-inquiry',
  general: 'https://car.amar.co.jp/api/authorization/admin-dashboard-panel/get-list-of-general-inquiry',
  shipping: 'https://car.amar.co.jp/api/authorization/admin-dashboard-panel/get-list-of-shipment-inquiry',
};

const MARK_INQUIRY_READ_ENDPOINT = 'https://car.amar.co.jp/api/authorization/admin-user/mark-inquiry-read';

const COUNT_ENDPOINTS = {
  shipping: 'https://car.amar.co.jp/api/authorization/admin-dashboard-panel/get-count-of-shipment-inquiry',
  general: 'https://car.amar.co.jp/api/authorization/admin-dashboard-panel/get-count-of-general-inquiry',
};

const AllInquiries = ({ onSendInvoice, autoOpenId, onAutoOpenConsumed, onNavigateToInvoice, onNavigateToOrder }) => {
  const [inquiries, setInquiries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [inquiryType, setInquiryType] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedInquiry, setSelectedInquiry] = useState(null);

  // --- Assigned To (UI-only mock, no backend yet — API pending) ---
  const [assignedAdminSelection, setAssignedAdminSelection] = useState('');
  // TODO: replace with a real fetch of Admin/Super Admin/Inquiry Admin users once the API is ready.
  const DUMMY_ADMIN_LIST = [
    { id: 1, name: 'Omneet Choudhary', role: 'SUPER_ADMIN' },
    { id: 2, name: 'Swapnil Yadav', role: 'ADMIN' },
    { id: 3, name: 'Hardik Paija', role: 'INQUIRY_ADMIN' },
    { id: 4, name: 'Harry Potter', role: 'INQUIRY_ADMIN' },
  ];
  const currentAdminRole = (typeof window !== 'undefined' && localStorage.getItem('adminRole')) || '';
  const canManageAssignment = currentAdminRole === 'SUPER_ADMIN' || currentAdminRole === 'ADMIN';

  useEffect(() => {
    if (selectedInquiry) {
      setAssignedAdminSelection(
        selectedInquiry.assignedInquiryAdminId ? String(selectedInquiry.assignedInquiryAdminId) : ''
      );
    }
  }, [selectedInquiry]);

  // Dropdown options: dummy list + the admin actually assigned in the API response.
  // Real IDs don't match the dummy IDs (e.g. Swapnil Yadav is 9 in API, 2 in dummy),
  // so the real assigned admin is added from the response and its dummy duplicate (same name) is dropped.
  const getAdminOptions = (inquiry) => {
    const assignedId = inquiry?.assignedInquiryAdminId;
    const assignedName = inquiry?.assignedInquiryAdminName;
    if (!assignedId || !assignedName) return DUMMY_ADMIN_LIST;
    const rest = DUMMY_ADMIN_LIST.filter(
      (admin) => String(admin.id) !== String(assignedId) && admin.name !== assignedName
    );
    return [{ id: assignedId, name: assignedName, role: '' }, ...rest];
  };

  const handleAssignedAdminChange = (e) => {
    const newId = e.target.value;
    setAssignedAdminSelection(newId);
    // TODO: wire up the save-assignment API call here once it's available.
    // e.g. PUT /admin-dashboard-panel/assign-inquiry/{inquiryId} with { assignedInquiryAdminId: newId }
  };
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [imageUrl, setImageUrl] = useState('');
  const [imageLoading, setImageLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [showInvoice, setShowInvoice] = useState(false);

  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  // Tab button counts (All / General / Shipping) — null until first load
  const [tabCounts, setTabCounts] = useState(null);
  // "All" count comes straight from get-all-inquiry's totalInquiry
  const [allInquiryTotal, setAllInquiryTotal] = useState(null);

  const fetchTabCounts = async () => {
    const headers = { 'accept': 'application/json', 'Accept-Language': 'en' };
    const [shipRes, genRes] = await Promise.allSettled([
      fetch(COUNT_ENDPOINTS.shipping, { headers }).then(r => (r.ok ? r.json() : null)),
      fetch(COUNT_ENDPOINTS.general, { headers }).then(r => (r.ok ? r.json() : null)),
    ]);
    const shipping = shipRes.status === 'fulfilled' ? shipRes.value?.totalShipmentInquiry : undefined;
    const general  = genRes.status === 'fulfilled' ? genRes.value?.totalGeneralInquiry : undefined;
    // Keep previous counts if both calls failed
    if (shipping === undefined && general === undefined) return;
    setTabCounts(prev => {
      const s = shipping ?? prev?.shipping ?? 0;
      const g = general ?? prev?.general ?? 0;
      return { shipping: s, general: g, all: s + g };
    });
  };

  useEffect(() => {
    fetchInquiries(0, 'all');
    fetchTabCounts();
  }, []);

  const fetchInquiries = async (page = 0, type = inquiryType) => {
    try {
      setLoading(true);
      setError(null);

      const endpoint = API_ENDPOINTS[type];
      const adminAuthToken = localStorage.getItem('adminAuthToken');
      const response = await fetch(
        `${endpoint}?page=${page}&size=${PAGE_SIZE}`,
        {
          method: 'GET',
          headers: {
            'accept': 'application/json',
            'Accept-Language': 'en',
            'Authorization': `Bearer ${adminAuthToken}`,
          },
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch inquiries');
      }

      const responseData = await response.json();

      setTotalPages(responseData.totalPages || 1);
      if (type === 'all' && typeof responseData.totalInquiry === 'number') {
        setAllInquiryTotal(responseData.totalInquiry);
      }

      const rawInquiries = responseData.data || [];

      const processedInquiries = rawInquiries.map(inquiry => {
        const isGuest = inquiry.userId === null;

        // Determine inquiry type based on data if fetching "all"
        let derivedType = 'general';
        if (type === 'shipping') {
          derivedType = 'shipping';
        } else if (type === 'general') {
          derivedType = 'general';
        } else {
          // "all" tab - derive type from data
          const hasShippingInfo =
            (inquiry.portId !== null && inquiry.portId !== undefined) ||
            (inquiry.shippingOption !== null && inquiry.shippingOption !== '' && inquiry.shippingOption !== 'null') ||
            (inquiry.baseShippingCost !== null && inquiry.baseShippingCost !== undefined && inquiry.baseShippingCost > 0);
          derivedType = hasShippingInfo ? 'shipping' : 'general';
        }

        const base = {
          ...inquiry,
          inquiryType: derivedType,
          isGuest,
          dateOfInquiry: normalizeDate(inquiry.dateOfInquiry),
          baseShippingCost: inquiry.baseShippingCost || 0,
          totalCost: inquiry.totalCost || null,
        };

        if (isGuest) {
          base.firstName = inquiry.guestFirstName || '';
          base.lastName = inquiry.guestLastName || '';
          base.userEmailId = inquiry.guestEmailId || '';
          base.userMobileNumber = inquiry.guestMobileNumber || '';
          base.countryCode = inquiry.guestCountryCode || null;
        }

        return base;
      });

      // Sort by ID descending (newest first)
      processedInquiries.sort((a, b) => b.id - a.id);

      setInquiries(processedInquiries);
      setTotalCount(processedInquiries.length);
      setCurrentPage(page);
      setLoading(false);
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  const normalizeDate = (dateString) => {
    if (!dateString) return 'N/A';
    if (dateString.includes('/')) {
      const parts = dateString.split('/');
      if (parts.length === 3) return `${parts[0]}-${parts[1]}-${parts[2]}`;
    }
    return dateString;
  };

  // Auto-refresh every 30s for current tab & page
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchInquiries(currentPage, inquiryType);
      fetchTabCounts();
    }, 30000);
    return () => clearInterval(intervalId);
  }, [currentPage, inquiryType]);

  // Handle tab change: reset to page 0 and fetch from correct API
  const handleTypeChange = (newType) => {
    if (newType === inquiryType) return;
    setInquiryType(newType);
    setCurrentPage(0);
    setFilterStatus('all');
    setSearchQuery('');
    fetchInquiries(0, newType);
  };

  useEffect(() => {
    if (autoOpenId && inquiries.length > 0 && !loading) {
      const inquiry = inquiries.find(
        inq => inq.id === autoOpenId || inq.id === Number(autoOpenId)
      );
      if (inquiry) {
        handleViewInquiry(inquiry);
        if (onAutoOpenConsumed) onAutoOpenConsumed();
      }
    }
  }, [autoOpenId, inquiries, loading]);

  const fetchCarImage = async (carModelId) => {
    setImageLoading(true);
    try {
      const response = await fetch(
        `https://car.amar.co.jp/api/image/car-exterior-images-1/get-image/${carModelId}`,
        { method: 'GET', headers: { 'accept': 'application/json', 'Accept-Language': 'en' } }
      );
      if (!response.ok) throw new Error('Failed to fetch car image');
      const blob = await response.blob();
      setImageUrl(URL.createObjectURL(blob));
    } catch (err) {
      console.error('Error fetching image:', err);
      setImageUrl('');
    } finally {
      setImageLoading(false);
    }
  };

  const handleViewInquiry = (inquiry) => {
    setSelectedInquiry(inquiry);
    setIsModalOpen(true);
    fetchCarImage(inquiry.carModelId);

    if (!inquiry.isInquiryRead) {
      markInquiryAsRead(inquiry.id);
    }
  };

  const markInquiryAsRead = async (inquiryId) => {
    // Optimistically update UI so highlight disappears immediately
    setInquiries(prev =>
      prev.map(inq => (inq.id === inquiryId ? { ...inq, isInquiryRead: true } : inq))
    );
    setSelectedInquiry(prev =>
      prev && prev.id === inquiryId ? { ...prev, isInquiryRead: true } : prev
    );

    try {
      const adminAuthToken = localStorage.getItem('adminAuthToken');
      const response = await fetch(`${MARK_INQUIRY_READ_ENDPOINT}/${inquiryId}`, {
        method: 'PUT',
        headers: {
          'accept': 'application/json',
          'Authorization': `Bearer ${adminAuthToken}`,
        },
      });
      if (!response.ok) throw new Error('Failed to mark inquiry as read');
    } catch (err) {
      console.error('Error marking inquiry as read:', err);
      // Revert optimistic update on failure
      setInquiries(prev =>
        prev.map(inq => (inq.id === inquiryId ? { ...inq, isInquiryRead: false } : inq))
      );
    }
  };

  const handleSendInvoice = (inquiry) => {
    if (onSendInvoice) onSendInvoice(inquiry);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedInquiry(null);
    setImageUrl('');
  };

  const isShippingInquiry = (inquiry) => {
    if (inquiry.inquiryType) return inquiry.inquiryType === 'shipping';
    const hasPortId = inquiry.portId !== null && inquiry.portId !== undefined;
    const hasShippingOption = inquiry.shippingOption !== null && inquiry.shippingOption !== '' && inquiry.shippingOption !== 'null';
    const hasShippingCost = inquiry.baseShippingCost !== null && inquiry.baseShippingCost !== undefined && inquiry.baseShippingCost > 0;
    const vehicleLength = inquiry.vehicleLength || 0;
    const vehicleWidth = inquiry.vehicleWidth || 0;
    const vehicleHeight = inquiry.vehicleHeight || 0;
    const hasVolumeCalculation = vehicleLength > 0 && vehicleWidth > 0 && vehicleHeight > 0;
    return hasPortId || hasShippingOption || hasShippingCost || hasVolumeCalculation;
  };

  const getDisplayCost = (inquiry) => {
    if (inquiry.totalCost !== null && inquiry.totalCost !== undefined) return `$${parseFloat(inquiry.totalCost).toFixed(2)}`;
    if (inquiry.carPrice !== null && inquiry.carPrice !== undefined) return `$${parseFloat(inquiry.carPrice).toFixed(2)}`;
    return 'N/A';
  };

  const getCustomerName = (inquiry) => {
    const firstName = inquiry.firstName || inquiry.guestFirstName || '';
    const lastName = inquiry.lastName || inquiry.guestLastName || '';
    const name = `${firstName} ${lastName}`.trim();
    if (name) return name;
    if (inquiry.userEmailId || inquiry.guestEmailId) return 'Anonymous User';
    return 'N/A';
  };

  const getCustomerEmail = (inquiry) => inquiry.userEmailId || inquiry.guestEmailId || 'N/A';
  // Inquiry-time details (what the user filled in the inquiry form)
  const getInquiryContactName = (inquiry) => {
    const name = `${inquiry.guestFirstName || ''} ${inquiry.guestLastName || ''}`.trim();
    return name || getCustomerName(inquiry);
  };
  const getInquiryContactEmail = (inquiry) => inquiry.guestEmailId || getCustomerEmail(inquiry);
  const getCustomerPhone = (inquiry) => inquiry.guestMobileNumber || inquiry.userMobileNumber || 'N/A';
  // Show country code together with number, e.g. "+91-9999999998"
  const formatPhoneWithCode = (code, number) => {
    if (!number || number === 'N/A') return 'N/A';
    return code ? `${code}-${number}` : number;
  };
  const getCountryCode = (inquiry) => inquiry.guestCountryCode || inquiry.countryCode || 'N/A';
  const getInquiryStatus = (inquiry) => inquiry.currentStatus || 'N/A';

  const getStatusClassName = (status) => {
    if (!status || status === 'N/A') return 'ai-unknown';
    return 'ai-' + status.toLowerCase().replace(/\s+/g, '-');
  };

  const availableStatuses = useMemo(() => {
    const statusSet = new Set();
    inquiries.forEach(inquiry => {
      const status = inquiry.currentStatus;
      if (status) statusSet.add(status);
    });
    return Array.from(statusSet).sort();
  }, [inquiries]);

  const unreadCount = useMemo(
    () => inquiries.filter(inq => !inq.isInquiryRead).length,
    [inquiries]
  );

  const isActionDisabled = (inquiry) => {
    const status = (inquiry.currentStatus || '').toLowerCase();
    return status.includes('confirmed') ||
      status.includes('final proforma send') ||
      status.includes('cancelled') ||
      status.includes('rejected');
  };

  const markAllAsRead = () => {
    const unreadIds = inquiries.filter(inq => !inq.isInquiryRead).map(inq => inq.id);
    unreadIds.forEach(id => markInquiryAsRead(id));
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 0 && newPage < totalPages && newPage !== currentPage) {
      fetchInquiries(newPage, inquiryType);
    }
  };

  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    if (totalPages <= maxVisible + 2) {
      for (let i = 0; i < totalPages; i++) pages.push(i);
    } else {
      pages.push(0);
      let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
      let end = Math.min(totalPages - 2, start + maxVisible - 1);
      start = Math.max(1, end - maxVisible + 1);
      if (start > 1) pages.push('...');
      for (let i = start; i <= end; i++) pages.push(i);
      if (end < totalPages - 2) pages.push('...');
      pages.push(totalPages - 1);
    }
    return pages;
  };

  // Client-side filtering only for status and search (type is handled server-side now)
  const filteredInquiries = inquiries.filter(inquiry => {
    const statusMatch = filterStatus === 'all' || inquiry.currentStatus === filterStatus;
    const customerName = getCustomerName(inquiry).toLowerCase();
    const customerEmail = getCustomerEmail(inquiry).toLowerCase();
    const carModel = inquiry.carModelName?.toLowerCase() || '';
    const carBrand = inquiry.carBrandName?.toLowerCase() || '';
    const searchMatch = searchQuery === '' ||
      customerName.includes(searchQuery.toLowerCase()) ||
      customerEmail.includes(searchQuery.toLowerCase()) ||
      carModel.includes(searchQuery.toLowerCase()) ||
      carBrand.includes(searchQuery.toLowerCase()) ||
      inquiry.id.toString().includes(searchQuery);
    return statusMatch && searchMatch;
  });

  // Column sorting (click a header) — matches the pattern used in
  // AllTickets.js / AllUsers.js / AllAdmins.js / AllOrders.js.
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const getSortIcon = (field) => {
    if (sortField !== field) return <span style={{ marginLeft: '4px', fontSize: '10px', color: 'white' }}>▲▼</span>;
    return <span style={{ marginLeft: '4px', fontSize: '11px', color: 'white' }}>{sortDirection === 'asc' ? '▲' : '▼'}</span>;
  };

  // date comes as "DD-MM-YYYY" — not natively parseable by `new Date(...)`.
  const parseInquiryDate = (dateStr) => {
    if (!dateStr || dateStr === 'N/A') return 0;
    const [dd, mm, yyyy] = dateStr.split('-').map(Number);
    if (!dd || !mm || !yyyy) return 0;
    return new Date(yyyy, mm - 1, dd).getTime();
  };

  const sortedFilteredInquiries = !sortField ? filteredInquiries : [...filteredInquiries].sort((a, b) => {
    if (sortField === 'id') {
      return sortDirection === 'asc' ? a.id - b.id : b.id - a.id;
    }
    if (sortField === 'date') {
      return sortDirection === 'asc'
        ? parseInquiryDate(a.dateOfInquiry) - parseInquiryDate(b.dateOfInquiry)
        : parseInquiryDate(b.dateOfInquiry) - parseInquiryDate(a.dateOfInquiry);
    }
    if (sortField === 'cost') {
      const aN = parseFloat(getDisplayCost(a).replace(/[^0-9.-]/g, '')) || 0;
      const bN = parseFloat(getDisplayCost(b).replace(/[^0-9.-]/g, '')) || 0;
      return sortDirection === 'asc' ? aN - bN : bN - aN;
    }
    const fieldMap = {
      customer: (x) => getCustomerName(x),
      email: (x) => getCustomerEmail(x),
      car: (x) => `${x.carBrandName || ''} ${x.carModelName || ''}`,
      type: (x) => (isShippingInquiry(x) ? 'Shipping' : 'General'),
      status: (x) => getInquiryStatus(x),
    };
    const aV = (fieldMap[sortField]?.(a) || '').toString().toLowerCase();
    const bV = (fieldMap[sortField]?.(b) || '').toString().toLowerCase();
    if (aV < bV) return sortDirection === 'asc' ? -1 : 1;
    if (aV > bV) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  const renderTabCount = (type) => {
    if (!tabCounts) return null;
    const isActive = inquiryType === type;
    return (
      <span
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          justifyContent: 'center',
          minWidth: '22px',
          height: '20px',
          padding: '0 7px',
          marginLeft: '8px',
          borderRadius: '999px',
          fontSize: '12px',
          fontWeight: 700,
          lineHeight: 1,
          background: isActive ? 'rgba(255,255,255,0.25)' : '#e5e7eb',
          color: isActive ? '#fff' : '#374151',
        }}
      >
        {type === 'all' ? (allInquiryTotal ?? tabCounts.all) : tabCounts[type]}
      </span>
    );
  };

  if (loading && inquiries.length === 0) {
    return (
      <div className="ai-dashboard-container">
        <div className="ai-loading-message">Loading inquiries...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="ai-dashboard-container">
        <div className="ai-error-message">
          <p>Error: {error}</p>
          <button onClick={() => fetchInquiries(currentPage, inquiryType)}>Retry</button>
        </div>
      </div>
    );
  }

  return (
    <div className="ai-dashboard-container" style={{ background: 'none', boxShadow: 'none', borderRadius: 0, border: 'none' }}>
      <div className="ai-dashboard-header" style={{ background: 'none', boxShadow: 'none', border: 'none', padding: 0 }}>
        <h2 style={{ margin: '0 0 20px 0', fontSize: '1.4rem', fontWeight: 700, color: '#0f172a' }}>
          Customer Inquiries ({filteredInquiries.length}/{totalCount})
          <span style={{ display: 'block', width: 36, height: 3, background: '#e11d48', borderRadius: 2, marginTop: 6 }}></span>
        </h2>
        {unreadCount > 0 && (
          <div className="ai-unread-badge">
            {unreadCount} New Inquiry{unreadCount > 1 ? 's' : ''}
            <button
              className="ai-mark-all-read-btn"
              onClick={markAllAsRead}
              style={{ marginLeft: '10px', fontSize: '12px', padding: '2px 8px' }}
            >
              Mark All Read
            </button>
          </div>
        )}
      </div>

      {/* Filters + table share one card */}
      <div style={{ backgroundColor: 'white', borderRadius: '8px', boxShadow: '0 2px 4px rgba(0,0,0,0.1)', padding: '20px' }}>
        <div className="ai-dashboard-actions">
          <div className="ai-type-buttons">
            <button
              className={`ai-type-btn ${inquiryType === 'all' ? 'active' : ''}`}
              onClick={() => handleTypeChange('all')}
            >
              All Inquiries
              {renderTabCount('all')}
            </button>
            <button
              className={`ai-type-btn ${inquiryType === 'general' ? 'active' : ''}`}
              onClick={() => handleTypeChange('general')}
            >
              General Inquiry
              {renderTabCount('general')}
            </button>
            <button
              className={`ai-type-btn ${inquiryType === 'shipping' ? 'active' : ''}`}
              onClick={() => handleTypeChange('shipping')}
            >
              Shipping Inquiry
              {renderTabCount('shipping')}
            </button>
          </div>

          <div className="ai-filter-dropdown">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="ai-filter-select"
            >
              <option value="all">All Status</option>
              {availableStatuses.map(status => (
                <option key={status} value={status}>{status}</option>
              ))}
            </select>
          </div>

          <input
            type="text"
            placeholder="Search by name, email, car, or ID..."
            className="ai-search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

      <div className="ai-table-container" style={{ background: 'none', boxShadow: 'none', border: 'none', padding: 0, marginTop: '20px' }}>
        {loading && (
          <div className="ai-pagination-loading-overlay">
            <span>Loading page {currentPage + 1}...</span>
          </div>
        )}
        {filteredInquiries.length === 0 && !loading ? (
          <div className="ai-no-results">
            No inquiries found matching your criteria.
          </div>
        ) : (
          <div style={{ overflowX: 'auto', overflowY: 'auto', maxHeight: '70vh' }}>
          <table className="ai-table">
            <thead style={{ position: 'sticky', top: 0, zIndex: 1 }}>
              <tr style={{ backgroundColor: '#d32f2f' }}>
                <th onClick={() => handleSort('id')} style={{ backgroundColor: '#d32f2f', color: 'white', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>INQUIRY ID{getSortIcon('id')}</th>
                <th onClick={() => handleSort('customer')} style={{ backgroundColor: '#d32f2f', color: 'white', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Customer{getSortIcon('customer')}</th>
                <th onClick={() => handleSort('email')} style={{ backgroundColor: '#d32f2f', color: 'white', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Email{getSortIcon('email')}</th>
                <th onClick={() => handleSort('car')} style={{ backgroundColor: '#d32f2f', color: 'white', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap' }}>Car Model{getSortIcon('car')}</th>
                <th onClick={() => handleSort('date')} style={{ backgroundColor: '#d32f2f', color: 'white', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', textAlign: 'center' }}>Date{getSortIcon('date')}</th>
                <th onClick={() => handleSort('type')} style={{ backgroundColor: '#d32f2f', color: 'white', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', textAlign: 'center' }}>Type{getSortIcon('type')}</th>
                <th onClick={() => handleSort('status')} style={{ backgroundColor: '#d32f2f', color: 'white', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', textAlign: 'center' }}>Status{getSortIcon('status')}</th>
                <th onClick={() => handleSort('cost')} style={{ backgroundColor: '#d32f2f', color: 'white', cursor: 'pointer', userSelect: 'none', whiteSpace: 'nowrap', textAlign: 'center' }}>Total Cost{getSortIcon('cost')}</th>
                <th style={{ backgroundColor: '#d32f2f', color: 'white', whiteSpace: 'nowrap', textAlign: 'center' }}>Assigned To</th>
                <th style={{ backgroundColor: '#d32f2f', color: 'white', textAlign: 'center' }}>Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedFilteredInquiries.map(inquiry => {
                const status = getInquiryStatus(inquiry);
                const statusClass = getStatusClassName(status);
                const isShipping = isShippingInquiry(inquiry);

                return (
                  <tr
                    key={inquiry.id}
                    className={`ai-row ${!inquiry.isInquiryRead ? 'ai-unread' : ''}`}
                  >
                    <td>{inquiry.id}</td>
                    <td>
                      <div className="ai-customer">
                        {getCustomerName(inquiry)}
                        {inquiry.isGuest && (
                          <span className="ai-guest-badge">Guest</span>
                        )}
                        {!inquiry.isInquiryRead && (
                          <span className="ai-unread-dot"></span>
                        )}
                      </div>
                    </td>
                    <td>{getCustomerEmail(inquiry)}</td>
                    <td>
                      <div className="ai-car-model-info">
                        <div className="ai-car-brand">{inquiry.carBrandName || 'N/A'}</div>
                        <div className="ai-car-model">{inquiry.carModelName || 'N/A'}</div>
                      </div>
                    </td>
                    <td style={{ textAlign: 'center' }}>{inquiry.dateOfInquiry}</td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`ai-type-badge ${isShipping ? 'ai-shipping' : 'ai-general'}`}>
                        {isShipping ? 'Shipping' : 'General'}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>
                      <span className={`ai-status-badge ${statusClass}`}>
                        {status}
                      </span>
                    </td>
                    <td style={{ textAlign: 'center' }}>{getDisplayCost(inquiry)}</td>
                    <td style={{ textAlign: 'center' }}>{inquiry.assignedInquiryAdminName || 'N/A'}</td>
                    <td style={{ textAlign: 'center' }}>
                      <div className="ai-action-buttons-group">
                        <button
                          className="ai-action-btn ai-view-btn"
                          onClick={() => handleViewInquiry(inquiry)}
                          title="View Details"
                        >
                          View
                        </button>
                        <button
                          className="ai-action-btn ai-invoice-btn"
                          onClick={() => handleSendInvoice(inquiry)}
                          title="Send Final Performa Invoice"
                          disabled={isActionDisabled(inquiry)}
                        >
                          Send Invoice
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </div>
        )}
      </div>
      </div>

      {totalPages > 1 && (
        <div className="ai-pagination-container">
          <div className="ai-pagination-info">
            Page {currentPage + 1} of {totalPages}
          </div>
          <div className="ai-pagination-buttons">
            <button
              className="ai-pagination-btn ai-pagination-prev"
              onClick={() => handlePageChange(currentPage - 1)}
              disabled={currentPage === 0 || loading}
            >
              ← Previous
            </button>

            {getPageNumbers().map((page, idx) =>
              page === '...' ? (
                <span key={`ellipsis-${idx}`} className="ai-pagination-ellipsis">...</span>
              ) : (
                <button
                  key={page}
                  className={`ai-pagination-btn ai-pagination-num ${currentPage === page ? 'active' : ''}`}
                  onClick={() => handlePageChange(page)}
                  disabled={loading}
                >
                  {page + 1}
                </button>
              )
            )}

            <button
              className="ai-pagination-btn ai-pagination-next"
              onClick={() => handlePageChange(currentPage + 1)}
              disabled={currentPage >= totalPages - 1 || loading}
            >
              Next →
            </button>
          </div>
        </div>
      )}

      {isModalOpen && selectedInquiry && (
        <div className="ai-modal-overlay">
          <div className="ai-details-modal">
            <div className="ai-modal-header">
              <h3>
                Inquiry Details - #{selectedInquiry.id}
                {selectedInquiry.isGuest && (
                  <span className="ai-guest-tag">Guest User</span>
                )}
              </h3>
              <button className="ai-close-btn" onClick={closeModal}>×</button>
            </div>

            <div className="ai-modal-content">
              <div className="ai-car-image-section">
                {imageLoading ? (
                  <div className="ai-loading-spinner">Loading image...</div>
                ) : imageUrl ? (
                  <img
                    src={imageUrl}
                    alt={`${selectedInquiry.carBrandName} ${selectedInquiry.carModelName}`}
                    className="ai-car-image"
                  />
                ) : (
                  <div className="ai-car-image-placeholder">
                    <img
                      src="/carousel/carslider.png"
                      alt="Car placeholder"
                      className="ai-placeholder-image"
                    />
                  </div>
                )}
                <div className="ai-car-basic-info">
                  <h4>{selectedInquiry.carBrandName} {selectedInquiry.carModelName}</h4>
                  <div className="ai-type-display">
                    <span className={`ai-type-badge ${isShippingInquiry(selectedInquiry) ? 'ai-shipping' : 'ai-general'}`}>
                      {isShippingInquiry(selectedInquiry) ? 'Shipping Calculation Inquiry' : 'General Inquiry'}
                    </span>
                  </div>
                  <div className="ai-car-meta">
                    <span>{selectedInquiry.carFuel || 'N/A'}</span>
                    <span>{selectedInquiry.carTransmission || 'N/A'}</span>
                    <span>{selectedInquiry.carMileage || '0'} km</span>
                    <span>{selectedInquiry.carColor || 'N/A'}</span>
                  </div>
                </div>
              </div>

              <div className="ai-details-sections">
                <div className="ai-details-section">
                  <h4 className="ai-section-title">Reference Numbers</h4>
                  <div className="ai-specs-grid">
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Inquiry Number</span>
                      <span className="ai-spec-value ai-ref-number-value">
                        {selectedInquiry.inquiryRefNumber || 'N/A'}
                      </span>
                    </div>
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Proforma Invoice Number</span>
                      {selectedInquiry.proformaInvoiceNo ? (
                        <span
                          className="ai-spec-value ai-ref-number-value ai-clickable-ref"
                          onClick={() => {
                            closeModal();
                            onNavigateToInvoice && onNavigateToInvoice(selectedInquiry.proformaInvoiceNo);
                          }}
                          title="View Proforma Invoice"
                        >
                          {selectedInquiry.proformaInvoiceNo}
                          <svg width="14" height="14" viewBox="0 0 14 14" style={{ marginLeft: '6px', verticalAlign: 'middle' }}>
                            <path d="M5.5 2L10 7L5.5 12" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      ) : (
                        <span className="ai-spec-value ai-ref-number-value">N/A</span>
                      )}
                    </div>
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Order Invoice Number</span>
                      {selectedInquiry.orderRefId ? (
                        <span
                          className="ai-spec-value ai-ref-number-value ai-clickable-ref"
                          onClick={() => {
                            closeModal();
                            onNavigateToOrder && onNavigateToOrder(selectedInquiry.orderRefId);
                          }}
                          title="View Order"
                        >
                          {selectedInquiry.orderRefId}
                          <svg width="14" height="14" viewBox="0 0 14 14" style={{ marginLeft: '6px', verticalAlign: 'middle' }}>
                            <path d="M5.5 2L10 7L5.5 12" stroke="currentColor" strokeWidth="2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                          </svg>
                        </span>
                      ) : (
                        <span className="ai-spec-value ai-ref-number-value">N/A</span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Customer Information — registered profile data */}
                <div className="ai-details-section">
                  <h4 className="ai-section-title">Customer Information</h4>
                  {selectedInquiry.isGuest ? (
                    <div className="ai-specs-grid">
                      <div className="ai-spec-item">
                        <span className="ai-spec-label">User Type</span>
                        <span className="ai-spec-value">Guest</span>
                      </div>
                      <div className="ai-spec-item">
                        <span className="ai-spec-label">Registered Profile</span>
                        <span className="ai-spec-value">Not available (guest inquiry)</span>
                      </div>
                    </div>
                  ) : (
                    <div className="ai-specs-grid">
                      <div className="ai-spec-item">
                        <span className="ai-spec-label">Name</span>
                        <span className="ai-spec-value">
                          {`${selectedInquiry.firstName || ''} ${selectedInquiry.lastName || ''}`.trim() || 'N/A'}
                        </span>
                      </div>
                      <div className="ai-spec-item">
                        <span className="ai-spec-label">Email</span>
                        <span className="ai-spec-value">{selectedInquiry.userEmailId || 'N/A'}</span>
                      </div>
                      <div className="ai-spec-item">
                        <span className="ai-spec-label">Phone</span>
                        <span className="ai-spec-value">
                          {formatPhoneWithCode(selectedInquiry.countryCode, selectedInquiry.userMobileNumber)}
                        </span>
                      </div>
                      <div className="ai-spec-item">
                        <span className="ai-spec-label">Country</span>
                        <span className="ai-spec-value">{selectedInquiry.registredUserCountryName || 'N/A'}</span>
                      </div>
                      <div className="ai-spec-item">
                        <span className="ai-spec-label">Gender</span>
                        <span className="ai-spec-value">{selectedInquiry.gender || 'N/A'}</span>
                      </div>
                      <div className="ai-spec-item">
                        <span className="ai-spec-label">Date of Birth</span>
                        <span className="ai-spec-value">{selectedInquiry.dateOfBirth || 'N/A'}</span>
                      </div>
                      <div className="ai-spec-item">
                        <span className="ai-spec-label">User Type</span>
                        <span className="ai-spec-value">Registered User</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* Inquiry Contact Details — data entered while making the inquiry */}
                <div className="ai-details-section">
                  <h4 className="ai-section-title">Inquiry Contact Details</h4>
                  <div className="ai-specs-grid">
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Name</span>
                      <span className="ai-spec-value">{getInquiryContactName(selectedInquiry)}</span>
                    </div>
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Email</span>
                      <span className="ai-spec-value">{getInquiryContactEmail(selectedInquiry)}</span>
                    </div>
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Phone</span>
                      <span className="ai-spec-value">
                        {formatPhoneWithCode(selectedInquiry.guestCountryCode, getCustomerPhone(selectedInquiry))}
                      </span>
                    </div>
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Country</span>
                      <span className="ai-spec-value">{selectedInquiry.countryName || 'N/A'}</span>
                    </div>
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Port</span>
                      <span className="ai-spec-value">{selectedInquiry.portName || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div className="ai-details-section">
                  <h4 className="ai-section-title">Vehicle Information</h4>
                  <div className="ai-specs-grid">
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Brand/Model</span>
                      <span className="ai-spec-value">{selectedInquiry.carBrandName} {selectedInquiry.carModelName}</span>
                    </div>
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Category</span>
                      <span className="ai-spec-value">{selectedInquiry.carCategoriesName || 'N/A'}</span>
                    </div>
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Model Code</span>
                      <span className="ai-spec-value">{selectedInquiry.carModelCode || 'N/A'}</span>
                    </div>
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Color</span>
                      <span className="ai-spec-value">{selectedInquiry.carColor || 'N/A'}</span>
                    </div>
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Registration Date</span>
                      <span className="ai-spec-value">{selectedInquiry.carRegistrationDate || 'N/A'}</span>
                    </div>
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Mileage</span>
                      <span className="ai-spec-value">{selectedInquiry.carMileage || 'N/A'} km</span>
                    </div>
                  </div>
                </div>

                <div className="ai-details-section">
                  <h4 className="ai-section-title">Technical Specifications</h4>
                  <div className="ai-specs-grid">
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Reference Number</span>
                      <span className="ai-spec-value">{selectedInquiry.carRefNumber || 'N/A'}</span>
                    </div>
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Chassis Number</span>
                      <span className="ai-spec-value">{selectedInquiry.carChassisNumber || 'N/A'}</span>
                    </div>
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Engine CC</span>
                      <span className="ai-spec-value">{selectedInquiry.carEngineCC || 'N/A'}</span>
                    </div>
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Doors</span>
                      <span className="ai-spec-value">{selectedInquiry.carDoor || 'N/A'}</span>
                    </div>
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Fuel Type</span>
                      <span className="ai-spec-value">{selectedInquiry.carFuel || 'N/A'}</span>
                    </div>
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Transmission</span>
                      <span className="ai-spec-value">{selectedInquiry.carTransmission || 'N/A'}</span>
                    </div>
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Steering</span>
                      <span className="ai-spec-value">{selectedInquiry.carSteering || 'N/A'}</span>
                    </div>
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Seats</span>
                      <span className="ai-spec-value">{selectedInquiry.carSeat || 'N/A'}</span>
                    </div>
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Drive Type</span>
                      <span className="ai-spec-value">{selectedInquiry.carDrive || 'N/A'}</span>
                    </div>
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Location</span>
                      <span className="ai-spec-value">{selectedInquiry.carLocation || 'N/A'}</span>
                    </div>
                  </div>
                </div>

                <div className="ai-details-section">
                  <h4 className="ai-section-title">Inquiry Status</h4>
                  <div className="ai-status-payment-container">
                    <div className="ai-status-display">
                      <span className="ai-status-label">Status:</span>
                      <span className={`ai-status-value ${getStatusClassName(getInquiryStatus(selectedInquiry))}`}>
                        {getInquiryStatus(selectedInquiry)}
                      </span>
                    </div>
                    <div className="ai-status-display">
                      <span className="ai-status-label">Inquiry Date:</span>
                      <span className="ai-status-value">{selectedInquiry.dateOfInquiry}</span>
                    </div>
                    <div className="ai-status-display">
                      <span className="ai-status-label">Inquiry Type:</span>
                      <span className="ai-status-value">
                        {isShippingInquiry(selectedInquiry) ? 'Shipping Calculation' : 'General Price Inquiry'}
                      </span>
                    </div>
                    {canManageAssignment && (
                      <div className="ai-status-display">
                        <span className="ai-status-label">Assigned To:</span>
                        <select
                          value={assignedAdminSelection}
                          onChange={handleAssignedAdminChange}
                          style={{
                            padding: '6px 10px',
                            borderRadius: '4px',
                            border: '1px solid #ddd',
                            fontSize: '13px',
                            fontWeight: 600,
                            color: '#333',
                            backgroundColor: 'white',
                            cursor: 'pointer',
                          }}
                        >
                          <option value="">Unassigned</option>
                          {getAdminOptions(selectedInquiry).map((admin) => (
                            <option key={admin.id} value={admin.id}>
                              {admin.name}{admin.role ? ` (${admin.role.replace('_', ' ')})` : ''}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                  </div>
                </div>

                {isShippingInquiry(selectedInquiry) && (
                  <div className="ai-details-section">
                    <h4 className="ai-section-title">Shipping Details</h4>
                    <div className="ai-specs-grid">
                      <div className="ai-spec-item">
                        <span className="ai-spec-label">Shipping Option</span>
                        <span className="ai-spec-value">{selectedInquiry.shippingOption || 'N/A'}</span>
                      </div>
                      <div className="ai-spec-item">
                        <span className="ai-spec-label">Base Shipping Cost</span>
                        <span className="ai-spec-value">
                          ${parseFloat(selectedInquiry.baseShippingCost || 0).toFixed(2)}
                        </span>
                      </div>
                      <div className="ai-spec-item">
                        <span className="ai-spec-label">Vehicle Volume</span>
                        <span className="ai-spec-value">
                          {selectedInquiry.volumeCubicMeters
                            ? `${selectedInquiry.volumeCubicMeters.toFixed(2)} m³`
                            : 'N/A'}
                        </span>
                      </div>
                      {selectedInquiry.shippingType && (
                        <div className="ai-spec-item">
                          <span className="ai-spec-label">Shipping Type</span>
                          <span className="ai-spec-value">{selectedInquiry.shippingType}</span>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                <div className="ai-details-section">
                  <h4 className="ai-section-title">Cost Summary</h4>
                  <div className="ai-specs-grid">
                    <div className="ai-spec-item">
                      <span className="ai-spec-label">Car Base Price</span>
                      <span className="ai-spec-value">
                        ${parseFloat(selectedInquiry.carPrice || 0).toFixed(2)}
                      </span>
                    </div>
                    {isShippingInquiry(selectedInquiry) && (
                      <div className="ai-spec-item">
                        <span className="ai-spec-label">Shipping Cost</span>
                        <span className="ai-spec-value">
                          ${parseFloat(selectedInquiry.baseShippingCost || 0).toFixed(2)}
                        </span>
                      </div>
                    )}
                    <div className="ai-spec-item ai-total-cost">
                      <span className="ai-spec-label">
                        {isShippingInquiry(selectedInquiry) ? 'Total Cost' : 'Car Price'}
                      </span>
                      <span className="ai-spec-value">
                        ${selectedInquiry.totalCost
                          ? parseFloat(selectedInquiry.totalCost).toFixed(2)
                          : parseFloat(selectedInquiry.carPrice || 0).toFixed(2)}
                      </span>
                    </div>
                  </div>
                </div>

                <div className="ai-details-section">
                  <h4 className="ai-section-title">Customer Message</h4>
                  <div className="ai-message-container">
                    <div className="ai-customer-message">
                      {selectedInquiry.userMessage || 'No message provided'}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="ai-modal-footer">
              <button className="ai-modal-close-btn" onClick={closeModal}>
                Close
              </button>
              <button
                className="ai-modal-invoice-btn"
                onClick={() => {
                  closeModal();
                  handleSendInvoice(selectedInquiry);
                }}
                disabled={isActionDisabled(selectedInquiry)}
              >
                Send Final Performa Invoice
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AllInquiries;